import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  Inbox,
  Loader2,
  Send,
  Sparkles,
  Trash2,
  X as XIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { api, ApiError, type ScrapeBatch, type SurfacedTweet, type Topic } from '@/lib/api'
import { charCount, formatRelative } from '@/lib/utils'
import { useAuth } from '@/lib/auth-context'

export default function Engagement() {
  const { me } = useAuth()
  const [params, setParams] = useSearchParams()
  const batchId = params.get('batch')

  const [items, setItems] = useState<SurfacedTweet[]>([])
  const [batches, setBatches] = useState<ScrapeBatch[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [loading, setLoading] = useState(true)
  const [showDiscarded, setShowDiscarded] = useState(false)

  const [tweetUrl, setTweetUrl] = useState('')
  const [tweetText, setTweetText] = useState('')
  const [authorHandle, setAuthorHandle] = useState('')
  const [capturing, setCapturing] = useState(false)

  const topicNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of topics) m.set(t.id, t.name)
    return m
  }, [topics])

  async function load() {
    try {
      const [i, b, t] = await Promise.all([
        api.listEngagement({ batchId: batchId ?? undefined, includeDiscarded: showDiscarded }),
        api.listBatches(),
        api.listTopics(),
      ])
      setItems(i)
      setBatches(b)
      setTopics(t)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    load()
  }, [batchId, showDiscarded])

  // Auto-refresh while any batch is still processing.
  const pollingRef = useRef<number | null>(null)
  useEffect(() => {
    const activelyProcessing = batches.some(
      (b) => b.processingStatus === 'pending' || b.processingStatus === 'processing',
    )
    if (activelyProcessing && pollingRef.current === null) {
      pollingRef.current = window.setInterval(load, 3000)
    }
    if (!activelyProcessing && pollingRef.current !== null) {
      window.clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [batches])

  async function capture() {
    setCapturing(true)
    try {
      await api.captureEngagement({
        tweetUrl: tweetUrl.trim(),
        tweetText: tweetText.trim(),
        authorHandle: authorHandle.replace(/^@/, '').trim(),
      })
      toast.success('Captured — draft replies ready below')
      setTweetUrl('')
      setTweetText('')
      setAuthorHandle('')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not capture')
    } finally {
      setCapturing(false)
    }
  }

  const activeBatch = batches.find((b) => b.id === batchId)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Engagement</h1>
          <p className="text-muted-foreground">
            Scrape tweets, Claude classifies + drafts replies, you approve the keepers.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowDiscarded((v) => !v)}
          title={showDiscarded ? 'Hide discarded' : 'Show discarded'}
        >
          {showDiscarded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showDiscarded ? 'Hide discarded' : 'Show discarded'}
        </Button>
      </div>

      {batchId ? (
        <Card className="bg-accent/40">
          <CardContent className="flex items-center justify-between pt-5">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <Inbox className="h-4 w-4" />
                Batch scrape
                {activeBatch && <BatchStatusBadge status={activeBatch.processingStatus} />}
              </div>
              <div className="text-xs text-muted-foreground">
                {activeBatch ? (
                  <>
                    {activeBatch.sourceKind} · {activeBatch.tweetCount} scraped ·{' '}
                    <strong>{activeBatch.keptCount} kept</strong>, {activeBatch.discardedCount} discarded ·{' '}
                    <a href={activeBatch.sourceUrl} target="_blank" rel="noreferrer" className="hover:underline">
                      {activeBatch.sourceUrl.replace(/^https?:\/\//, '').slice(0, 48)}
                    </a>{' '}
                    · {formatRelative(activeBatch.createdAt)}
                  </>
                ) : (
                  'Loading…'
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                params.delete('batch')
                setParams(params, { replace: true })
              }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to inbox
            </Button>
          </CardContent>
        </Card>
      ) : (
        batches.length > 0 && <BatchStrip batches={batches} />
      )}

      {!batchId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Capture a tweet manually</CardTitle>
            <CardDescription>
              Or use the{' '}
              <Link to="/app/settings" className="text-primary hover:underline">bookmarklet</Link>{' '}
              — one click on any x.com page auto-classifies + drafts.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="url" className="text-xs">Tweet URL</Label>
                <Input id="url" placeholder="https://x.com/jane/status/1234567890" value={tweetUrl} onChange={(e) => setTweetUrl(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="author" className="text-xs">Author handle</Label>
                <Input id="author" placeholder="@jane" value={authorHandle} onChange={(e) => setAuthorHandle(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="text" className="text-xs">Tweet text</Label>
              <Textarea id="text" rows={3} placeholder="Paste the tweet's text here" value={tweetText} onChange={(e) => setTweetText(e.target.value)} />
            </div>
            <Button
              onClick={capture}
              disabled={capturing || !tweetUrl.trim() || !tweetText.trim() || !authorHandle.trim()}
            >
              <Sparkles className="h-4 w-4" />
              {capturing ? 'Drafting…' : 'Draft replies'}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
            {batchId ? 'No keepers in this batch. Try "Show discarded" to see what Claude filtered out.' : 'No tweets captured yet. Install the bookmarklet to make this fast.'}
          </p>
        )}
        {items.map((item) => (
          <TweetCard
            key={item.id}
            item={item}
            topicName={item.topicId ? topicNameById.get(item.topicId) ?? null : null}
            onChange={load}
            xConnected={me?.x.connected ?? false}
          />
        ))}
      </div>
    </div>
  )
}

function BatchStatusBadge({ status }: { status: ScrapeBatch['processingStatus'] }) {
  if (status === 'processing' || status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
        <Loader2 className="h-2.5 w-2.5 animate-spin" /> classifying
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
        failed
      </span>
    )
  }
  return null
}

function BatchStrip({ batches }: { batches: ScrapeBatch[] }) {
  const recent = batches.slice(0, 6)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Recent batches</CardTitle>
        <CardDescription className="text-xs">From the bookmarklet</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {recent.map((b) => (
          <Link
            key={b.id}
            to={`/app/engagement?batch=${b.id}`}
            className="rounded-md border bg-background px-3 py-2 text-xs hover:bg-accent"
          >
            <div className="flex items-center gap-1.5 font-medium">
              {b.sourceKind} · {b.tweetCount} scraped
              <BatchStatusBadge status={b.processingStatus} />
            </div>
            <div className="text-muted-foreground">
              {b.keptCount} kept · {formatRelative(b.createdAt)}
            </div>
          </Link>
        ))}
      </CardContent>
    </Card>
  )
}

function TopicBadge({ name, score }: { name: string | null; score: number | null }) {
  if (!name && score === null) return null
  const color =
    score !== null && score >= 8 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    : score !== null && score >= 5 ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
    : 'bg-muted text-muted-foreground'
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}>
      {name ?? 'unclassified'}
      {score !== null && <span className="opacity-70">· {score}/10</span>}
    </span>
  )
}

function TweetCard({
  item,
  topicName,
  onChange,
  xConnected,
}: {
  item: SurfacedTweet
  topicName: string | null
  onChange: () => void
  xConnected: boolean
}) {
  const [drafting, setDrafting] = useState(false)
  const hasDrafts = item.drafts.some((d) => d.status !== 'rejected')
  const isDiscarded = item.processingStatus === 'discarded'
  const isProcessing = item.processingStatus === 'raw' || item.processingStatus === 'classified'

  async function draft() {
    setDrafting(true)
    try {
      await api.draftForTweet(item.id)
      toast.success('Drafts ready')
      onChange()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not draft')
    } finally {
      setDrafting(false)
    }
  }

  return (
    <Card className={isDiscarded ? 'opacity-60 border-dashed' : undefined}>
      <CardContent className="space-y-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>@{item.authorHandle}</span>
              <span>· {formatRelative(item.capturedAt)}</span>
              <TopicBadge name={topicName} score={item.topicScore} />
              {isDiscarded && <span className="text-amber-700 dark:text-amber-400">discarded</span>}
              {isProcessing && !hasDrafts && (
                <span className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300">
                  <Loader2 className="h-3 w-3 animate-spin" /> drafting
                </span>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm">{item.tweetText}</p>
            {item.topicReason && (
              <p className="mt-1 text-[11px] italic text-muted-foreground">"{item.topicReason}"</p>
            )}
            <a
              href={item.tweetUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Open on X
            </a>
          </div>
          <div className="flex items-center gap-1">
            {!hasDrafts && !isProcessing && (
              <Button size="sm" onClick={draft} disabled={drafting}>
                <Sparkles className="h-3.5 w-3.5" />
                {drafting ? 'Drafting…' : 'Draft'}
              </Button>
            )}
            <button
              onClick={async () => {
                if (!confirm('Remove this tweet and all its drafts?')) return
                await api.deleteSurfaced(item.id)
                onChange()
              }}
              className="text-muted-foreground hover:text-destructive"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {hasDrafts && (
          <div className="grid gap-2.5 md:grid-cols-3">
            {item.drafts.map((d) => (
              <DraftCard
                key={d.id}
                draft={d}
                tweetId={item.tweetId}
                xConnected={xConnected}
                onChange={onChange}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function DraftCard({
  draft,
  tweetId,
  xConnected,
  onChange,
}: {
  draft: SurfacedTweet['drafts'][number]
  tweetId: string
  xConnected: boolean
  onChange: () => void
}) {
  const [body, setBody] = useState(draft.body)
  const [busy, setBusy] = useState(false)

  async function send() {
    if (!xConnected) {
      toast.error('Connect your X account first')
      return
    }
    setBusy(true)
    try {
      await api.approveDraft({ draftId: draft.id, body, sendNow: true })
      toast.success('Reply sent')
      onChange()
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        // X credits depleted — fall back to the compose-on-X flow with text copied.
        try { await navigator.clipboard.writeText(body) } catch { /* ignore */ }
        window.open(
          `https://x.com/intent/tweet?in_reply_to=${tweetId}&text=${encodeURIComponent(body)}`,
          '_blank',
        )
        toast.info('X API credits depleted. Reply copied — paste, post, then click "Mark posted".', {
          duration: 10000,
          action: {
            label: 'Mark posted',
            onClick: () => markPosted(),
          },
        })
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Could not send')
      }
    } finally {
      setBusy(false)
    }
  }

  async function reject() {
    setBusy(true)
    try {
      await api.rejectDraft(draft.id)
      onChange()
    } finally {
      setBusy(false)
    }
  }

  async function markPosted() {
    setBusy(true)
    try {
      await api.markDraftSent(draft.id, body)
      toast.success('Marked as posted')
      onChange()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not mark')
    } finally {
      setBusy(false)
    }
  }

  const readonly = draft.status !== 'pending'
  const intentUrl = `https://x.com/intent/tweet?in_reply_to=${tweetId}&text=${encodeURIComponent(body)}`

  return (
    <div
      className={`flex flex-col rounded-md border p-3 text-sm ${
        draft.status === 'sent'
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/20'
          : draft.status === 'rejected'
          ? 'border-dashed opacity-60'
          : 'bg-background'
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>Variant {draft.variantIdx + 1}</span>
        <span>{draft.status}</span>
      </div>
      <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} disabled={readonly} className="flex-1 text-sm" />
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span className={charCount(body) > 280 ? 'text-destructive' : ''}>{charCount(body)}/280</span>
        {!readonly && (
          <div className="flex items-center gap-1">
            <a
              href={intentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent"
              title="Open X compose instead of posting via API"
              onClick={() => {
                try { navigator.clipboard.writeText(body) } catch { /* ignore */ }
                toast.info('Reply copied. Post it on X, then click the check to mark sent.', {
                  duration: 10000,
                  action: { label: 'Mark posted', onClick: () => markPosted() },
                })
              }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button
              size="sm"
              variant="ghost"
              onClick={markPosted}
              disabled={busy || !body.trim()}
              title="I already posted this on X — mark sent"
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={reject} disabled={busy} title="Reject">
              <XIcon className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" onClick={send} disabled={busy || !body.trim() || charCount(body) > 280} title="Post via API">
              <Send className="h-3.5 w-3.5" />
              Send
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
